import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const GeographicalNamingCalculator = () => {
  const [nameData, setNameData] = useState({
    locationName: '',
    regionType: '',
    geographicalFeature: '',
    historicalContext: '',
    culturalSignificance: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNameData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const generateGeographicalName = () => {
    const { locationName, regionType, geographicalFeature, historicalContext, culturalSignificance } = nameData;
    
    if (!locationName.trim() || !regionType.trim() || !geographicalFeature.trim()) {
      setError('Please enter all required fields');
      return;
    }

    // Generate geographical name components
    const geographicalNameComponents = {
      fullName: `${locationName} (${regionType}) - ${geographicalFeature}${historicalContext ? ` - ${historicalContext}` : ''}${culturalSignificance ? ` - ${culturalSignificance}` : ''}`,
      shortName: `${locationName} - ${geographicalFeature}`,
      locationTagline: `${geographicalFeature}${historicalContext ? ` - ${historicalContext}` : ''}${culturalSignificance ? ` - ${culturalSignificance}` : ''}`,
      geographicalIdentity: `${locationName} (${regionType}) - ${geographicalFeature}`,
      locationStatement: `${locationName} - ${geographicalFeature} - ${historicalContext || culturalSignificance}`
    };

    // Generate comprehensive naming rules
    const namingRules = {
      location: 'Location name should be accurate and officially recognized. Use local language names when appropriate.',
      region: 'Region type should clearly define the geographical context (e.g., city, state, country, region).',
      feature: 'Geographical feature should accurately describe the physical characteristics of the location.',
      history: 'Historical context should provide relevant historical background that adds depth to the location name.',
      culture: 'Cultural significance should reflect important cultural aspects or traditions associated with the location.',
      identity: 'Geographical identity should be consistent across all official documents and maps.',
      statement: 'Location statement should clearly communicate the geographical and cultural context of the place.'
    };

    // Generate practical example usage
    const exampleUsage = {
      cartography: `Use ${geographicalNameComponents.fullName} in all maps and atlases. Highlight ${geographicalFeature} in your key features.`,
      tourism: `Promote ${locationName} on tourism materials targeting ${regionType}. Use ${geographicalFeature} as your main attraction.`,
      education: `Teach about ${geographicalNameComponents.geographicalIdentity} in geography lessons. Include ${historicalContext} in history lessons.`,
      research: `Use ${geographicalNameComponents.locationStatement} in academic papers. Reference ${geographicalFeature} in geographical studies.`,
      government: `Use ${geographicalNameComponents.fullName} in official documents. Reference ${geographicalFeature} in policy documents.`,
      navigation: `Use ${geographicalNameComponents.shortName} in navigation systems. Highlight ${geographicalFeature} in route descriptions.`,
      documentation: `Use ${geographicalNameComponents.geographicalIdentity} in legal documents. Reference ${historicalContext} in historical records.`
    };

    // Generate detailed calculation steps
    const calculationSteps = [
      'Step 1: Analyze Geographical Components',
      `Location Name: ${locationName} (Should be accurate and officially recognized)`,
      `Region Type: ${regionType} (Defines geographical context)`,
      `Geographical Feature: ${geographicalFeature} (Physical characteristics)`,
      `Historical Context: ${historicalContext || 'None'} (Historical background)`,
      `Cultural Significance: ${culturalSignificance || 'None'} (Cultural aspects)`,
      '\nStep 2: Create Geographical Identity',
      `Full Name: ${geographicalNameComponents.fullName} (Complete geographical representation)`,
      `Short Name: ${geographicalNameComponents.shortName} (Concise identifier)`,
      `Location Tagline: ${geographicalNameComponents.locationTagline} (Feature description)`,
      `Geographical Identity: ${geographicalNameComponents.geographicalIdentity} (Core identity)`,
      `Location Statement: ${geographicalNameComponents.locationStatement} (Context statement)`,
      '\nStep 3: Apply Naming Rules',
      `Location: ${namingRules.location}`,
      `Region: ${namingRules.region}`,
      `Feature: ${namingRules.feature}`,
      `History: ${namingRules.history}`,
      `Culture: ${namingRules.culture}`,
      `Identity: ${namingRules.identity}`,
      `Statement: ${namingRules.statement}`
    ];

    // Generate enhanced chart data
    const chartData = {
      bar: {
        labels: ['Location', 'Region', 'Feature', 'History', 'Culture', 'Identity', 'Statement'],
        datasets: [{
          label: 'Geographical Components',
          data: [1, 2, 3, historicalContext ? 4 : 0, culturalSignificance ? 5 : 0, 6, 7],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)',
            historicalContext ? 'rgba(153, 102, 255, 0.5)' : 'rgba(0, 0, 0, 0)',
            culturalSignificance ? 'rgba(255, 159, 64, 0.5)' : 'rgba(0, 0, 0, 0)',
            'rgba(231, 74, 128, 0.5)',
            'rgba(255, 205, 86, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            historicalContext ? 'rgba(153, 102, 255, 1)' : 'rgba(0, 0, 0, 0)',
            culturalSignificance ? 'rgba(255, 159, 64, 1)' : 'rgba(0, 0, 0, 0)',
            'rgba(231, 74, 128, 1)',
            'rgba(255, 205, 86, 1)'
          ],
          borderWidth: 1
        }]
      },
      pie: {
        labels: ['Location', 'Region', 'Feature', 'History', 'Culture', 'Identity', 'Statement'],
        datasets: [{
          data: [1, 1, 1, historicalContext ? 1 : 0, culturalSignificance ? 1 : 0, 1, 1],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)',
            'rgba(255, 159, 64, 0.7)',
            historicalContext ? 'rgba(153, 102, 255, 0.7)' : 'rgba(0, 0, 0, 0)',
            culturalSignificance ? 'rgba(201, 203, 207, 0.7)' : 'rgba(0, 0, 0, 0)',
            'rgba(231, 74, 128, 0.7)',
            'rgba(255, 205, 86, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(255, 159, 64, 1)',
            historicalContext ? 'rgba(153, 102, 255, 1)' : 'rgba(0, 0, 0, 0)',
            culturalSignificance ? 'rgba(201, 203, 207, 1)' : 'rgba(0, 0, 0, 0)',
            'rgba(231, 74, 128, 1)',
            'rgba(255, 205, 86, 1)'
          ],
          borderWidth: 1
        }]
      }
    };

    const newResults = {
      geographicalNameComponents,
      namingRules,
      exampleUsage,
      calculationSteps
    };

    setResults(newResults);
    setChartData(chartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Geographical Naming Calculator | Generate Location Names</title>
        <meta name="description" content="Generate geographical names with our Geographical Naming Calculator. Get accurate naming conventions for locations, regions, and features." />
        <meta name="keywords" content="geographical naming, location naming, regional naming, geographical features, historical context, cultural significance" />
        <meta property="og:title" content="Geographical Naming Calculator | Generate Location Names" />
        <meta property="og:description" content="Generate geographical names with our Geographical Naming Calculator. Get accurate naming conventions for locations, regions, and features." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/geographical-naming-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🗺️</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Geographical Naming Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Generate Accurate Location Names</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator helps you generate geographical names using standardized naming conventions. It provides naming rules and examples for different geographical components.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="locationName">
            Location Name:
          </label>
          <input
            type="text"
            id="locationName"
            name="locationName"
            value={nameData.locationName}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the location name (e.g., Paris, New York, Tokyo)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="regionType">
            Region Type:
          </label>
          <input
            type="text"
            id="regionType"
            name="regionType"
            value={nameData.regionType}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the region type (e.g., City, State, Country, Region)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="geographicalFeature">
            Geographical Feature:
          </label>
          <input
            type="text"
            id="geographicalFeature"
            name="geographicalFeature"
            value={nameData.geographicalFeature}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the geographical feature (e.g., River, Mountain, Coast)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="historicalContext">
            Historical Context (optional):
          </label>
          <input
            type="text"
            id="historicalContext"
            name="historicalContext"
            value={nameData.historicalContext}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter historical context (e.g., Founded in 1200, Ancient Capital)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="culturalSignificance">
            Cultural Significance (optional):
          </label>
          <input
            type="text"
            id="culturalSignificance"
            name="culturalSignificance"
            value={nameData.culturalSignificance}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter cultural significance (e.g., Cultural Center, Historical Site)"
          />
        </div>

        <button
          onClick={generateGeographicalName}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Generate Geographical Name
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="calculator-section mt-12"
          >
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 gap-6 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Geographical Name</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Full Name</span>
                      <span className="font-medium">{results.geographicalNameComponents.fullName}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Short Name</span>
                      <span className="font-medium">{results.geographicalNameComponents.shortName}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Location Tagline</span>
                      <span className="font-medium">{results.geographicalNameComponents.locationTagline}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Geographical Identity</span>
                      <span className="font-medium">{results.geographicalNameComponents.geographicalIdentity}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Location Statement</span>
                      <span className="font-medium">{results.geographicalNameComponents.locationStatement}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Naming Rules</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Location</span>
                      <span className="font-medium">{results.namingRules.location}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Region</span>
                      <span className="font-medium">{results.namingRules.region}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Feature</span>
                      <span className="font-medium">{results.namingRules.feature}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">History</span>
                      <span className="font-medium">{results.namingRules.history}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Culture</span>
                      <span className="font-medium">{results.namingRules.culture}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Identity</span>
                      <span className="font-medium">{results.namingRules.identity}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Statement</span>
                      <span className="font-medium">{results.namingRules.statement}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Example Usage</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Cartography</span>
                      <span className="font-medium">{results.exampleUsage.cartography}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Tourism</span>
                      <span className="font-medium">{results.exampleUsage.tourism}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Education</span>
                      <span className="font-medium">{results.exampleUsage.education}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Research</span>
                      <span className="font-medium">{results.exampleUsage.research}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Government</span>
                      <span className="font-medium">{results.exampleUsage.government}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Navigation</span>
                      <span className="font-medium">{results.exampleUsage.navigation}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Documentation</span>
                      <span className="font-medium">{results.exampleUsage.documentation}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Component Chart</h4>
                  <div className="h-96">
                    <Bar
                      data={chartData.bar}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Component Level'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Geographical Component'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Geographical Component Levels'
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold mb-4">Naming Chart</h4>
                  <div className="h-96">
                    <Pie
                      data={chartData.pie}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Geographical Components'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-12 bg-gray-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Geographical Naming Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Geographical Naming Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Geographical Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the location name</li>
                <li>Input the region type (required)</li>
                <li>Input the geographical feature (required)</li>
                <li>Optionally add historical context</li>
                <li>Optionally add cultural significance</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Generate Geographical Name</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Generate Geographical Name" button</li>
                <li>Review the generated geographical name</li>
                <li>Check the naming rules</li>
                <li>Verify the example usage</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the full name, short name, and location tagline</li>
                <li>Review the naming rules for each component</li>
                <li>Verify the example usage for different scenarios</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is geographical naming?</h3>
              <p className="text-gray-700">
                Geographical naming is the process of creating standardized names for locations, regions, and geographical features. It helps in maintaining consistency across maps, documents, and official communications.
              </p>
            </div>

            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What components are required?</h3>
              <p className="text-gray-700">
                The minimum required components are:
                <br/><br/>
                - Location Name: Official name of the place
                <br/>
                - Region Type: Type of geographical region (e.g., City, State, Country)
                <br/>
                - Geographical Feature: Physical characteristics of the location
              </p>
            </div>

            <div className="border-l-4 border-yellow-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">Why is geographical naming important?</h3>
              <p className="text-gray-700">
                Geographical naming helps in:
                <br/><br/>
                - Standardizing location references
                <br/>
                - Improving navigation and mapping
                <br/>
                - Preserving historical and cultural context
                <br/>
                - Enhancing geographical education
              </p>
            </div>

            <div className="border-l-4 border-purple-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is historical context?</h3>
              <p className="text-gray-700">
                Historical context provides important background information about the location's past, including founding dates, significant events, and historical significance.
              </p>
            </div>

            <div className="border-l-4 border-red-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is cultural significance?</h3>
              <p className="text-gray-700">
                Cultural significance highlights important cultural aspects, traditions, or heritage associated with the location, helping to preserve and promote cultural identity.
              </p>
            </div>

            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">How do I choose a region type?</h3>
              <p className="text-gray-700">
                Common approaches for defining region type:
                <br/><br/>
                - Administrative: Based on official administrative divisions
                <br/>
                - Geographical: Based on physical geographical features
                <br/>
                - Historical: Based on historical regions or territories
                <br/>
                - Cultural: Based on cultural or linguistic regions
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Generate standardized geographical names</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Support for historical context</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Cultural significance integration</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Region-based naming</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Feature-specific naming</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-green-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Consistent geographical references</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Improved navigation and mapping</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Preserved historical context</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Enhanced cultural awareness</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Standardized documentation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Geographical Naming <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Geographical naming is a systematic approach to creating standardized names for locations, regions, and geographical features. It involves careful consideration of various components to ensure the names are accurate, consistent, and meaningful.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Location Name:</span> Official identifier for the place
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Region Type:</span> Defines the geographical context
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Geographical Feature:</span> Physical characteristics of the location
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Historical Context:</span> Background information about the location's history
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-purple-600 rounded-full mr-2"></span>
              <span className="font-medium">Cultural Significance:</span> Cultural aspects and traditions associated with the location
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Geographical names are typically used in maps, atlases, navigation systems, and official documents to maintain consistent location references.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GeographicalNamingCalculator;